import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Flex,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Switch,
  NumberInput,
  NumberInputField,
  NumberIncrementStepper,
  NumberDecrementStepper,
  NumberInputStepper,
  Badge,
  InputGroup,
  InputLeftElement,
  Textarea,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  useColorModeValue,
  Checkbox,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { 
  FaFlask, 
  FaVial,
  FaSearch,
  FaSave,
  FaEdit,
  FaArrowLeft,
  FaDollarSign,
  FaExternalLinkAlt
} from 'react-icons/fa';

const LabTestCostsManagement = () => {
  const { labId } = useParams(); // Get lab ID from URL parameters
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const mainBg = useColorModeValue('gray.50', 'gray.900');
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  
  const [selectedLab, setSelectedLab] = useState(null);
  const [tests, setTests] = useState([]);
  const [panels, setPanels] = useState([]);
  const [testOfferings, setTestOfferings] = useState([]);
  const [panelOfferings, setPanelOfferings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Search within tests/panels (when a lab is selected)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // Lab selection: search-first approach (no initial load)
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [labSearchResults, setLabSearchResults] = useState([]);
  const [isLabSearching, setIsLabSearching] = useState(false);
  const [labPage, setLabPage] = useState(1);
  // Browse mode: server-side pagination
  const [browseLabs, setBrowseLabs] = useState([]);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [isBrowsingLoading, setIsBrowsingLoading] = useState(false);
  const [totalLabsCount, setTotalLabsCount] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  // Quick filters (when a lab is selected)
  const [filterOfferedOnly, setFilterOfferedOnly] = useState(false);
  const [filterMissingPrices, setFilterMissingPrices] = useState(false);
  // Bulk selection
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());
  const [selectedPanelIds, setSelectedPanelIds] = useState(new Set());
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  const [isBulkApplying, setIsBulkApplying] = useState(false);

  useEffect(() => {
    if (selectedLab) {
      fetchLabData(selectedLab.id);
      setSelectedTestIds(new Set());
      setSelectedPanelIds(new Set());
    }
  }, [selectedLab]);

  // Auto-select lab if labId is provided in URL - fetch that specific lab
  useEffect(() => {
    if (labId && !selectedLab) {
      fetchSpecificLab(labId);
    }
  }, [labId]);

  // Fetch a specific lab by ID (for URL params)
  const fetchSpecificLab = async (labIdToFetch) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .eq('id', labIdToFetch)
        .single();

      if (error) throw error;
      if (data) {
        setSelectedLab(data);
      } else {
        toast({
          title: 'Lab not found',
          description: 'The specified lab could not be found.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/admin/lab-test-costs');
      }
    } catch (error) {
      toast({
        title: 'Error fetching lab',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/admin/lab-test-costs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabData = async (labId) => {
    setLoading(true);
    try {
      // Fetch all tests and panels from master directory
      const { data: testsData, error: testsError } = await supabase
        .from('tests')
        .select('*')
        .order('name');

      const { data: panelsData, error: panelsError } = await supabase
        .from('panels')
        .select('*')
        .order('name');

      if (testsError) throw testsError;
      if (panelsError) throw panelsError;

      // Fetch lab's current test offerings
      const { data: testOfferingsData, error: testOfferingsError } = await supabase
        .from('lab_test_offerings')
        .select('*')
        .eq('lab_id', labId);

      // Fetch lab's current panel offerings
      const { data: panelOfferingsData, error: panelOfferingsError } = await supabase
        .from('lab_panel_offerings')
        .select('*')
        .eq('lab_id', labId);

      if (testOfferingsError) throw testOfferingsError;
      if (panelOfferingsError) throw panelOfferingsError;

      setTests(testsData || []);
      setPanels(panelsData || []);
      setTestOfferings(testOfferingsData || []);
      setPanelOfferings(panelOfferingsData || []);

    } catch (error) {
      toast({
        title: 'Error fetching lab data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getTestOffering = (testId) => {
    return testOfferings.find(offering => offering.test_id === testId) || {
      is_offered: false,
      lab_test_code: '',
      lab_cash_price: null,
      lab_notes: ''
    };
  };

  const getPanelOffering = (panelId) => {
    return panelOfferings.find(offering => offering.panel_id === panelId) || {
      is_offered: false,
      lab_panel_code: '',
      lab_cash_price: null,
      lab_notes: ''
    };
  };

  const handleTestOfferingChange = async (testId, field, value) => {
    const currentOffering = getTestOffering(testId);
    const updatedOffering = { ...currentOffering, [field]: value };
    
    // Update local state
    setTestOfferings(prev => {
      const index = prev.findIndex(offering => offering.test_id === testId);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      } else {
        return [...prev, {
          lab_id: selectedLab.id,
          test_id: testId,
          ...updatedOffering
        }];
      }
    });

    // Save to database
    try {
      const { error } = await supabase
        .from('lab_test_offerings')
        .upsert({
          lab_id: selectedLab.id,
          test_id: testId,
          ...updatedOffering
        }, { onConflict: 'lab_id,test_id' });

      if (error) throw error;

      if (field === 'is_offered') {
        toast({
          title: value ? 'Test enabled' : 'Test disabled',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error updating test offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePanelOfferingChange = async (panelId, field, value) => {
    const currentOffering = getPanelOffering(panelId);
    const updatedOffering = { ...currentOffering, [field]: value };
    
    // Update local state
    setPanelOfferings(prev => {
      const index = prev.findIndex(offering => offering.panel_id === panelId);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      } else {
        return [...prev, {
          lab_id: selectedLab.id,
          panel_id: panelId,
          ...updatedOffering
        }];
      }
    });

    // Save to database
    try {
      const { error } = await supabase
        .from('lab_panel_offerings')
        .upsert({
          lab_id: selectedLab.id,
          panel_id: panelId,
          ...updatedOffering
        }, { onConflict: 'lab_id,panel_id' });

      if (error) throw error;

      if (field === 'is_offered') {
        toast({
          title: value ? 'Panel enabled' : 'Panel disabled',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error updating panel offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditItem = (item, type) => {
    setEditingItem({ ...item, type });
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    try {
      if (editingItem.type === 'test') {
        await handleTestOfferingChange(editingItem.id, 'lab_test_code', editingItem.lab_test_code);
        await handleTestOfferingChange(editingItem.id, 'lab_cash_price', editingItem.lab_cash_price);
        await handleTestOfferingChange(editingItem.id, 'lab_notes', editingItem.lab_notes);
      } else {
        await handlePanelOfferingChange(editingItem.id, 'lab_panel_code', editingItem.lab_panel_code);
        await handlePanelOfferingChange(editingItem.id, 'lab_cash_price', editingItem.lab_cash_price);
        await handlePanelOfferingChange(editingItem.id, 'lab_notes', editingItem.lab_notes);
      }
      onEditClose();
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error saving item',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleTestSelection = (id) => {
    setSelectedTestIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePanelSelection = (id) => {
    setSelectedPanelIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllDisplayTests = () => {
    if (selectedTestIds.size === displayTests.length) {
      setSelectedTestIds(new Set());
    } else {
      setSelectedTestIds(new Set(displayTests.map(t => t.id)));
    }
  };

  const selectAllDisplayPanels = () => {
    if (selectedPanelIds.size === displayPanels.length) {
      setSelectedPanelIds(new Set());
    } else {
      setSelectedPanelIds(new Set(displayPanels.map(p => p.id)));
    }
  };

  const bulkEnableTests = async (enable) => {
    const count = selectedTestIds.size;
    if (!selectedLab || count === 0) return;
    setIsBulkApplying(true);
    try {
      for (const testId of selectedTestIds) {
        await handleTestOfferingChange(testId, 'is_offered', enable);
      }
      setSelectedTestIds(new Set());
      toast({
        title: enable ? 'Tests enabled' : 'Tests disabled',
        description: `${count} test(s) updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error', isClosable: true });
    } finally {
      setIsBulkApplying(false);
    }
  };

  const bulkSetPriceTests = async () => {
    const price = parseFloat(bulkPriceValue);
    const count = selectedTestIds.size;
    if (!selectedLab || count === 0 || isNaN(price) || price < 0) {
      toast({ title: 'Enter a valid price', status: 'warning', isClosable: true });
      return;
    }
    setIsBulkApplying(true);
    try {
      for (const testId of selectedTestIds) {
        await handleTestOfferingChange(testId, 'lab_cash_price', price);
      }
      setBulkPriceValue('');
      setSelectedTestIds(new Set());
      toast({
        title: 'Prices updated',
        description: `Set $${price.toFixed(2)} for ${count} test(s).`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error', isClosable: true });
    } finally {
      setIsBulkApplying(false);
    }
  };

  const bulkEnablePanels = async (enable) => {
    const count = selectedPanelIds.size;
    if (!selectedLab || count === 0) return;
    setIsBulkApplying(true);
    try {
      for (const panelId of selectedPanelIds) {
        await handlePanelOfferingChange(panelId, 'is_offered', enable);
      }
      setSelectedPanelIds(new Set());
      toast({
        title: enable ? 'Panels enabled' : 'Panels disabled',
        description: `${count} panel(s) updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error', isClosable: true });
    } finally {
      setIsBulkApplying(false);
    }
  };

  const bulkSetPricePanels = async () => {
    const price = parseFloat(bulkPriceValue);
    const count = selectedPanelIds.size;
    if (!selectedLab || count === 0 || isNaN(price) || price < 0) {
      toast({ title: 'Enter a valid price', status: 'warning', isClosable: true });
      return;
    }
    setIsBulkApplying(true);
    try {
      for (const panelId of selectedPanelIds) {
        await handlePanelOfferingChange(panelId, 'lab_cash_price', price);
      }
      setBulkPriceValue('');
      setSelectedPanelIds(new Set());
      toast({
        title: 'Prices updated',
        description: `Set $${price.toFixed(2)} for ${count} panel(s).`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error', isClosable: true });
    } finally {
      setIsBulkApplying(false);
    }
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (test.description && test.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredPanels = panels.filter(panel => {
    const matchesSearch = panel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (panel.description && panel.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || panel.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Apply quick filters (offered only, missing prices) for display
  const displayTests = filteredTests.filter(test => {
    const offering = getTestOffering(test.id);
    if (filterOfferedOnly && !offering.is_offered) return false;
    if (filterMissingPrices) {
      if (!offering.is_offered) return false;
      const hasPrice = offering.lab_cash_price != null && offering.lab_cash_price !== '';
      if (hasPrice) return false;
    }
    return true;
  });

  const displayPanels = filteredPanels.filter(panel => {
    const offering = getPanelOffering(panel.id);
    if (filterOfferedOnly && !offering.is_offered) return false;
    if (filterMissingPrices) {
      if (!offering.is_offered) return false;
      const hasPrice = offering.lab_cash_price != null && offering.lab_cash_price !== '';
      if (hasPrice) return false;
    }
    return true;
  });

  // Stats for selected lab
  const statsOfferedTests = testOfferings.filter(o => o.is_offered).length;
  const statsOfferedPanels = panelOfferings.filter(o => o.is_offered).length;
  const statsWithPriceTests = testOfferings.filter(o => o.is_offered && o.lab_cash_price != null && o.lab_cash_price !== '').length;
  const statsWithPricePanels = panelOfferings.filter(o => o.is_offered && o.lab_cash_price != null && o.lab_cash_price !== '').length;

  const categories = ['all', ...new Set([...tests.map(t => t.category), ...panels.map(p => p.category)].filter(Boolean))];

  // Determine which labs to show: search results or browse results
  const labsToDisplay = isBrowsing ? browseLabs : labSearchResults;
  const labsPerPage = 50;
  
  // For search results: client-side pagination (already limited to 200)
  // For browse: server-side pagination (already fetched for current page)
  const pagedLabsForSelection = isBrowsing 
    ? labsToDisplay // Already paginated server-side
    : labsToDisplay.slice((labPage - 1) * labsPerPage, labPage * labsPerPage);
  
  const totalLabPages = isBrowsing
    ? Math.max(1, Math.ceil((totalLabsCount || 0) / labsPerPage))
    : Math.max(1, Math.ceil(labsToDisplay.length / labsPerPage));
  
  const currentLabPage = Math.min(labPage, totalLabPages);

  // Search across all labs in the database (primary way to find labs)
  const performLabSearch = async (query) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setLabSearchResults([]);
      setIsBrowsing(false);
      return;
    }

    setIsLabSearching(true);
    setIsBrowsing(false);
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .or(
          `name.ilike.%${trimmed}%,` +
          `city.ilike.%${trimmed}%,` +
          `state.ilike.%${trimmed}%,` +
          `contact_email.ilike.%${trimmed}%,` +
          `contact_phone.ilike.%${trimmed}%`
        )
        .order('name')
        .limit(200); // Reasonable cap - enough results to find the lab

      if (error) throw error;

      setLabSearchResults(data || []);
      setLabPage(1);

      if (data && data.length === 0) {
        toast({
          title: 'No labs found',
          description: `No labs match "${trimmed}". Try a different search term.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Lab search error:', error);
      toast({
        title: 'Search error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLabSearching(false);
    }
  };

  // Browse labs from start (server-side pagination)
  const fetchBrowsePage = async (page = 1) => {
    setIsBrowsingLoading(true);
    setIsBrowsing(true);
    try {
      const labsPerPage = 50;
      const start = (page - 1) * labsPerPage;
      const end = start + labsPerPage - 1;

      // Fetch labs for this page and total count in parallel
      const [labsResult, countResult] = await Promise.all([
        supabase
          .from('labs')
          .select('*')
          .order('name')
          .range(start, end),
        supabase
          .from('labs')
          .select('*', { count: 'exact', head: true })
      ]);

      if (labsResult.error) throw labsResult.error;
      if (countResult.error) throw countResult.error;

      setBrowseLabs(labsResult.data || []);
      setTotalLabsCount(countResult.count || 0);
      setLabPage(page);
      setLabSearchResults([]);
    } catch (error) {
      console.error('Browse labs error:', error);
      toast({
        title: 'Error loading labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBrowsingLoading(false);
    }
  };

  const TestRow = ({ test, offering, isSelected, onToggleSelect }) => (
    <Tr 
      bg={isSelected ? 'blue.50' : 'white'}
      _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
      transition="background 0.2s"
    >
      <Td>
        <Checkbox 
          isChecked={isSelected} 
          onChange={onToggleSelect} 
          aria-label={`Select ${test.name}`}
        />
      </Td>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.800">
            {test.name}
          </Text>
          {test.description && (
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {test.description}
            </Text>
          )}
        </VStack>
      </Td>
      <Td>
        <Badge colorScheme="blue" variant="subtle" fontSize="xs">Test</Badge>
      </Td>
      <Td>
        <Switch
          isChecked={offering.is_offered}
          onChange={(e) => handleTestOfferingChange(test.id, 'is_offered', e.target.checked)}
          colorScheme="green"
          size="md"
        />
      </Td>
      <Td>
        <Input
          size="sm"
          placeholder="Enter code"
          value={offering.lab_test_code || ''}
          onChange={(e) => handleTestOfferingChange(test.id, 'lab_test_code', e.target.value)}
          isDisabled={!offering.is_offered}
          bg={offering.is_offered ? 'white' : 'gray.100'}
          maxW="120px"
        />
      </Td>
      <Td>
        <NumberInput
          size="sm"
          min={0}
          precision={2}
          value={offering.lab_cash_price || ''}
          onChange={(value) => handleTestOfferingChange(test.id, 'lab_cash_price', parseFloat(value) || null)}
          isDisabled={!offering.is_offered}
          maxW="130px"
        >
          <NumberInputField 
            placeholder="$0.00" 
            bg={offering.is_offered ? 'white' : 'gray.100'}
            fontWeight={offering.lab_cash_price ? 'semibold' : 'normal'}
            color={offering.lab_cash_price ? 'green.600' : 'gray.400'}
          />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <Button
          size="sm"
          leftIcon={<FaEdit />}
          onClick={() => handleEditItem({ ...test, ...offering }, 'test')}
          isDisabled={!offering.is_offered}
          variant="ghost"
          colorScheme="blue"
        >
          Edit
        </Button>
      </Td>
    </Tr>
  );

  const PanelRow = ({ panel, offering, isSelected, onToggleSelect }) => (
    <Tr 
      bg={isSelected ? 'purple.50' : 'white'}
      _hover={{ bg: isSelected ? 'purple.100' : 'gray.50' }}
      transition="background 0.2s"
    >
      <Td>
        <Checkbox 
          isChecked={isSelected} 
          onChange={onToggleSelect} 
          aria-label={`Select ${panel.name}`}
        />
      </Td>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.800">
            {panel.name}
          </Text>
          {panel.description && (
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {panel.description}
            </Text>
          )}
        </VStack>
      </Td>
      <Td>
        <Badge colorScheme="purple" variant="subtle" fontSize="xs">Panel</Badge>
      </Td>
      <Td>
        <Switch
          isChecked={offering.is_offered}
          onChange={(e) => handlePanelOfferingChange(panel.id, 'is_offered', e.target.checked)}
          colorScheme="green"
          size="md"
        />
      </Td>
      <Td>
        <Input
          size="sm"
          placeholder="Enter code"
          value={offering.lab_panel_code || ''}
          onChange={(e) => handlePanelOfferingChange(panel.id, 'lab_panel_code', e.target.value)}
          isDisabled={!offering.is_offered}
          bg={offering.is_offered ? 'white' : 'gray.100'}
          maxW="120px"
        />
      </Td>
      <Td>
        <NumberInput
          size="sm"
          min={0}
          precision={2}
          value={offering.lab_cash_price || ''}
          onChange={(value) => handlePanelOfferingChange(panel.id, 'lab_cash_price', parseFloat(value) || null)}
          isDisabled={!offering.is_offered}
          maxW="130px"
        >
          <NumberInputField 
            placeholder="$0.00" 
            bg={offering.is_offered ? 'white' : 'gray.100'}
            fontWeight={offering.lab_cash_price ? 'semibold' : 'normal'}
            color={offering.lab_cash_price ? 'green.600' : 'gray.400'}
          />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <Button
          size="sm"
          leftIcon={<FaEdit />}
          onClick={() => handleEditItem({ ...panel, ...offering }, 'panel')}
          isDisabled={!offering.is_offered}
          variant="ghost"
          colorScheme="purple"
        >
          Edit
        </Button>
      </Td>
    </Tr>
  );

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} py={6} bg={mainBg} minH="100vh">
      {/* Header */}
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <HStack spacing={4}>
          <Box
            p={3}
            borderRadius="lg"
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            boxShadow="md"
          >
            <Icon as={FaDollarSign} boxSize={6} color="white" />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="xl" fontWeight="bold" color="gray.800">
              Lab Test Costs Management
            </Heading>
            <Text color="gray.600" fontSize="sm" mt={1}>
              Configure test offerings and pricing for labs
            </Text>
          </VStack>
        </HStack>
        {selectedLab && (
          <Button
            leftIcon={<FaArrowLeft />}
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              setSelectedLab(null);
              navigate('/admin/lab-test-costs');
            }}
          >
            ← Change Lab
          </Button>
        )}
      </Flex>

      {/* Lab Selection or Direct Lab Access */}
      {!selectedLab ? (
        <Card bg={cardBg} shadow="sm" borderRadius="lg">
          <CardBody>
            <VStack spacing={5} align="stretch">
              <VStack align="flex-start" spacing={1}>
                <Heading size="lg" textAlign="left">
                  Select a Lab to Manage
                </Heading>
                <Text fontSize="sm" color="gray.600">
                  Search across all laboratories, then pick one to configure its test menu and pricing.
                </Text>
              </VStack>

              {/* Lab search bar - search-first approach */}
              <VStack
                w="full"
                align="stretch"
                spacing={3}
                bg="gray.50"
                borderRadius="md"
                px={4}
                py={4}
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Search by name, email, city, or state across 19,000+ labs
                </Text>
                <Flex w="full" align="center" justify="center" gap={3} flexWrap="wrap">
                  <InputGroup maxW="500px" flex="1" minW={{ base: '100%', md: '300px' }}>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search labs by name, email, city, or state..."
                      value={labSearchTerm}
                      onChange={(e) => setLabSearchTerm(e.target.value)}
                      size="md"
                      bg="white"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && labSearchTerm.trim()) {
                          performLabSearch(labSearchTerm);
                        }
                      }}
                    />
                  </InputGroup>
                  <Button
                    size="md"
                    colorScheme="blue"
                    isLoading={isLabSearching}
                    isDisabled={!labSearchTerm.trim()}
                    onClick={() => performLabSearch(labSearchTerm)}
                    leftIcon={<FaSearch />}
                  >
                    Search
                  </Button>
                  <Button
                    size="md"
                    variant="outline"
                    isLoading={isBrowsingLoading}
                    onClick={() => fetchBrowsePage(1)}
                    leftIcon={<FaFlask />}
                  >
                    Browse from start
                  </Button>
                </Flex>
                {(labSearchResults.length > 0 || browseLabs.length > 0) && (
                  <Flex w="full" align="center" justify="space-between" fontSize="sm" color="gray.600">
                    <Text>
                      {isBrowsing
                        ? `Showing ${pagedLabsForSelection.length} of ${totalLabsCount?.toLocaleString() || '19,000+'} labs`
                        : `Showing ${pagedLabsForSelection.length} of ${labSearchResults.length} search result${labSearchResults.length === 1 ? '' : 's'}`}
                    </Text>
                    {(labSearchResults.length > 0 || isBrowsing) && (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setLabSearchResults([]);
                          setBrowseLabs([]);
                          setIsBrowsing(false);
                          setLabSearchTerm('');
                          setLabPage(1);
                          setTotalLabsCount(null);
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Flex>
                )}
              </VStack>

              {/* Show labs grid only if we have results */}
              {pagedLabsForSelection.length > 0 ? (
                <>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="full">
                    {pagedLabsForSelection.map(lab => (
                      <Card 
                        key={lab.id} 
                        cursor="pointer" 
                        onClick={() => setSelectedLab(lab)}
                        _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                        transition="all 0.2s"
                      >
                        <CardBody>
                          <VStack spacing={3}>
                            <Icon as={FaFlask} boxSize={6} color="blue.500" />
                            <VStack spacing={1} w="full">
                              <Text fontWeight="medium" textAlign="center">
                                {lab.name}
                              </Text>
                              <Text fontSize="sm" color="gray.600" textAlign="center">
                                {lab.city && lab.state ? `${lab.city}, ${lab.state}` : lab.address || 'Location not set'}
                              </Text>
                              <Text fontSize="sm" color="gray.600" textAlign="center">
                                {lab.contact_phone || 'No phone'}
                              </Text>
                              <Text fontSize="xs" color="gray.500" textAlign="center" noOfLines={1}>
                                {lab.contact_email || 'No email'}
                              </Text>
                            </VStack>
                            <Button colorScheme="blue" size="sm" w="full">
                              Manage Tests
                            </Button>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>

                  {/* Pagination */}
                  {totalLabPages > 1 && (
                    <HStack justify="space-between" align="center" mt={4} fontSize="sm">
                      <Text color="gray.600">
                        Page {currentLabPage} of {totalLabPages}
                      </Text>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newPage = currentLabPage - 1;
                            if (isBrowsing) {
                              fetchBrowsePage(newPage);
                            } else {
                              setLabPage(newPage);
                            }
                          }}
                          isDisabled={currentLabPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const newPage = currentLabPage + 1;
                            if (isBrowsing) {
                              fetchBrowsePage(newPage);
                            } else {
                              setLabPage(newPage);
                            }
                          }}
                          isDisabled={currentLabPage === totalLabPages}
                        >
                          Next
                        </Button>
                      </HStack>
                    </HStack>
                  )}
                </>
              ) : (
                // Empty state - show when no search/browse results
                !isLabSearching && !isBrowsingLoading && (
                  <Box textAlign="center" py={12} px={4}>
                    <Icon as={FaSearch} boxSize={12} color="gray.300" mb={4} />
                    <Text fontSize="lg" color="gray.600" mb={2}>
                      No labs to display
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {labSearchTerm.trim() 
                        ? `No labs found matching "${labSearchTerm}". Try a different search term.`
                        : 'Search for a lab by name, city, state, or email, or click "Browse from start" to view labs alphabetically.'}
                    </Text>
                  </Box>
                )
              )}

              {/* Loading state */}
              {(isLabSearching || isBrowsingLoading) && (
                <Box textAlign="center" py={12}>
                  <Spinner size="lg" color="blue.500" />
                  <Text mt={4} color="gray.600">
                    {isBrowsingLoading ? 'Loading labs...' : 'Searching labs...'}
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      ) : (
        /* Lab-Specific Test Management Interface */
        <VStack spacing={6} align="stretch">
          {/* Lab Info Card - Redesigned */}
          <Card bg={cardBg} boxShadow="lg" borderRadius="xl" overflow="hidden">
            <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" p={6} color="white">
              <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={4}>
                <VStack align="start" spacing={2}>
                  <HStack spacing={3}>
                    <Icon as={FaFlask} boxSize={6} />
                    <Heading size="lg" color="white" fontWeight="bold">
                      {selectedLab.name}
                    </Heading>
                  </HStack>
                  <Text fontSize="sm" color="whiteAlpha.900">
                    {selectedLab.city && selectedLab.state
                      ? `${selectedLab.city}, ${selectedLab.state}`
                      : selectedLab.address || 'Location not set'}
                  </Text>
                  <HStack spacing={4} fontSize="sm" color="whiteAlpha.900" flexWrap="wrap">
                    <Text>{selectedLab.contact_phone || 'No phone'}</Text>
                    {selectedLab.contact_email && (
                      <Text color="whiteAlpha.900">{selectedLab.contact_email}</Text>
                    )}
                  </HStack>
                </VStack>
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    variant="solid"
                    bg="whiteAlpha.200"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    onClick={() => navigate('/admin/labs')}
                    size="sm"
                  >
                    View Details
                  </Button>
                  <Button
                    variant="solid"
                    bg="whiteAlpha.200"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    leftIcon={<Icon as={FaExternalLinkAlt} />}
                    onClick={() => window.open(`/lab-directory/${selectedLab.id}`, '_blank')}
                    size="sm"
                  >
                    Lab Directory
                  </Button>
                </HStack>
              </Flex>
            </Box>
            <CardBody>
              {/* Stats Grid - Redesigned */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={4}>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="blue.50"
                  borderLeft="4px solid"
                  borderColor="blue.500"
                >
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={1}>
                    Tests Offered
                  </Text>
                  <Text fontWeight="bold" fontSize="2xl" color="blue.600">
                    {statsOfferedTests}
                  </Text>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="green.50"
                  borderLeft="4px solid"
                  borderColor="green.500"
                >
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={1}>
                    Tests with Price
                  </Text>
                  <Text fontWeight="bold" fontSize="2xl" color="green.600">
                    {statsWithPriceTests}
                  </Text>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="purple.50"
                  borderLeft="4px solid"
                  borderColor="purple.500"
                >
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={1}>
                    Panels Offered
                  </Text>
                  <Text fontWeight="bold" fontSize="2xl" color="purple.600">
                    {statsOfferedPanels}
                  </Text>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  bg="orange.50"
                  borderLeft="4px solid"
                  borderColor="orange.500"
                >
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={1}>
                    Panels with Price
                  </Text>
                  <Text fontWeight="bold" fontSize="2xl" color="orange.600">
                    {statsWithPricePanels}
                  </Text>
                </Box>
              </SimpleGrid>

              {/* Partnership & Subscription Info */}
              <HStack spacing={4} mt={4} flexWrap="wrap">
                <Badge 
                  colorScheme={selectedLab.partnership_status === 'claimed' ? 'green' : 'gray'}
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                >
                  Partnership: {selectedLab.partnership_status || 'unclaimed'}
                </Badge>
                <Badge 
                  colorScheme={selectedLab.subscription_status === 'active' ? 'green' : 'gray'}
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                >
                  Subscription: {selectedLab.subscription_status || 'inactive'}
                </Badge>
                {selectedLab.subscription_plan && (
                  <Badge 
                    colorScheme="blue"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                  >
                    Plan: {selectedLab.subscription_plan} (${selectedLab.monthly_fee || 0})
                  </Badge>
                )}
              </HStack>
            </CardBody>
          </Card>

          {/* Search and Filters - Redesigned */}
          <Card bg="gray.50" borderRadius="lg">
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search tests and panels..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="white"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </InputGroup>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </Select>
                <HStack spacing={4} flexWrap="wrap">
                  <Checkbox
                    isChecked={filterOfferedOnly}
                    onChange={(e) => setFilterOfferedOnly(e.target.checked)}
                    colorScheme="green"
                  >
                    <Text fontSize="sm" fontWeight="medium">Offered only</Text>
                  </Checkbox>
                  <Checkbox
                    isChecked={filterMissingPrices}
                    onChange={(e) => setFilterMissingPrices(e.target.checked)}
                    colorScheme="orange"
                  >
                    <Text fontSize="sm" fontWeight="medium">Missing prices</Text>
                  </Checkbox>
                </HStack>
              </SimpleGrid>
              <Alert status="info" variant="subtle" mt={4} borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm" fontWeight="medium">Changes are saved automatically</Text>
              </Alert>
            </CardBody>
          </Card>

          {/* Tests and Panels Table - Redesigned */}
          <Card bg={cardBg} boxShadow="lg" borderRadius="xl">
            <CardBody p={0}>
              <Tabs colorScheme="blue">
                <Box px={6} pt={6} pb={0}>
                  <TabList borderBottom="2px solid" borderColor="gray.200">
                    <Tab 
                      _selected={{ color: 'blue.600', borderColor: 'blue.600', fontWeight: 'bold' }}
                      fontSize="md"
                      py={4}
                      px={6}
                    >
                      Individual Tests ({displayTests.length})
                    </Tab>
                    <Tab 
                      _selected={{ color: 'purple.600', borderColor: 'purple.600', fontWeight: 'bold' }}
                      fontSize="md"
                      py={4}
                      px={6}
                    >
                      Test Panels ({displayPanels.length})
                    </Tab>
                  </TabList>
                </Box>

                <TabPanels>
                  <TabPanel px={6} py={6}>
                    {(selectedTestIds.size > 0) && (
                      <Box mb={4} p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
                        <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="blue.700">
                            {selectedTestIds.size} test(s) selected
                          </Text>
                          <Button size="sm" variant="ghost" onClick={() => setSelectedTestIds(new Set())}>
                            Clear Selection
                          </Button>
                        </HStack>
                        <HStack spacing={2} flexWrap="wrap" gap={2}>
                          <Button size="sm" colorScheme="green" onClick={() => bulkEnableTests(true)} isLoading={isBulkApplying}>
                            Enable Selected
                          </Button>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => bulkEnableTests(false)} isLoading={isBulkApplying}>
                            Disable Selected
                          </Button>
                          <HStack>
                            <Input 
                              size="sm" 
                              w="100px" 
                              placeholder="$0.00" 
                              value={bulkPriceValue} 
                              onChange={(e) => setBulkPriceValue(e.target.value)} 
                              type="number" 
                              min={0} 
                              step={0.01}
                              bg="white"
                            />
                            <Button size="sm" colorScheme="blue" onClick={bulkSetPriceTests} isLoading={isBulkApplying}>
                              Set Price
                            </Button>
                          </HStack>
                        </HStack>
                      </Box>
                    )}
                    <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th w="50px" py={4}>
                              <Checkbox 
                                isChecked={displayTests.length > 0 && selectedTestIds.size === displayTests.length} 
                                isIndeterminate={selectedTestIds.size > 0 && selectedTestIds.size < displayTests.length} 
                                onChange={selectAllDisplayTests} 
                                aria-label="Select all"
                                colorScheme="blue"
                              />
                            </Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Test Name</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Type</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Offered</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Lab Code</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Lab Price</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {displayTests.length === 0 ? (
                            <Tr>
                              <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                                No tests found matching your filters
                              </Td>
                            </Tr>
                          ) : (
                            displayTests.map(test => (
                              <TestRow
                                key={test.id}
                                test={test}
                                offering={getTestOffering(test.id)}
                                isSelected={selectedTestIds.has(test.id)}
                                onToggleSelect={() => toggleTestSelection(test.id)}
                              />
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>

                  <TabPanel px={6} py={6}>
                    {(selectedPanelIds.size > 0) && (
                      <Box mb={4} p={4} bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.200">
                        <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" fontWeight="bold" color="purple.700">
                            {selectedPanelIds.size} panel(s) selected
                          </Text>
                          <Button size="sm" variant="ghost" onClick={() => setSelectedPanelIds(new Set())}>
                            Clear Selection
                          </Button>
                        </HStack>
                        <HStack spacing={2} flexWrap="wrap" gap={2}>
                          <Button size="sm" colorScheme="green" onClick={() => bulkEnablePanels(true)} isLoading={isBulkApplying}>
                            Enable Selected
                          </Button>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => bulkEnablePanels(false)} isLoading={isBulkApplying}>
                            Disable Selected
                          </Button>
                          <HStack>
                            <Input 
                              size="sm" 
                              w="100px" 
                              placeholder="$0.00" 
                              value={bulkPriceValue} 
                              onChange={(e) => setBulkPriceValue(e.target.value)} 
                              type="number" 
                              min={0} 
                              step={0.01}
                              bg="white"
                            />
                            <Button size="sm" colorScheme="purple" onClick={bulkSetPricePanels} isLoading={isBulkApplying}>
                              Set Price
                            </Button>
                          </HStack>
                        </HStack>
                      </Box>
                    )}
                    <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th w="50px" py={4}>
                              <Checkbox 
                                isChecked={displayPanels.length > 0 && selectedPanelIds.size === displayPanels.length} 
                                isIndeterminate={selectedPanelIds.size > 0 && selectedPanelIds.size < displayPanels.length} 
                                onChange={selectAllDisplayPanels} 
                                aria-label="Select all"
                                colorScheme="purple"
                              />
                            </Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Panel Name</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Type</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Offered</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Lab Code</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Lab Price</Th>
                            <Th py={4} fontWeight="bold" color="gray.700">Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {displayPanels.length === 0 ? (
                            <Tr>
                              <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                                No panels found matching your filters
                              </Td>
                            </Tr>
                          ) : (
                            displayPanels.map(panel => (
                              <PanelRow
                                key={panel.id}
                                panel={panel}
                                offering={getPanelOffering(panel.id)}
                                isSelected={selectedPanelIds.has(panel.id)}
                                onToggleSelect={() => togglePanelSelection(panel.id)}
                              />
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      )}

      {/* Edit Modal - Redesigned */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <Box
            bg={editingItem?.type === 'test' 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            }
            p={6}
            color="white"
          >
            <ModalHeader color="white" pb={2}>
              <HStack spacing={2}>
                <Icon as={editingItem?.type === 'test' ? FaVial : FaFlask} boxSize={5} />
                <Text>
                  Edit {editingItem?.type === 'test' ? 'Test' : 'Panel'}
                </Text>
              </HStack>
              <Text fontSize="sm" color="whiteAlpha.900" fontWeight="normal" mt={1}>
                {editingItem?.name}
              </Text>
            </ModalHeader>
            <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />
          </Box>
          <ModalBody py={6}>
            <VStack spacing={5}>
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Lab Code
                </FormLabel>
                <Input
                  value={editingItem?.type === 'test' ? (editingItem?.lab_test_code || '') : (editingItem?.lab_panel_code || '')}
                  onChange={(e) => {
                    const field = editingItem?.type === 'test' ? 'lab_test_code' : 'lab_panel_code';
                    setEditingItem(prev => ({ ...prev, [field]: e.target.value }));
                  }}
                  placeholder="Enter lab's internal code"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Lab Price ($)
                </FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  value={editingItem?.lab_cash_price || ''}
                  onChange={(value) => setEditingItem(prev => ({ ...prev, lab_cash_price: parseFloat(value) || null }))}
                >
                  <NumberInputField 
                    placeholder="0.00" 
                    borderColor="gray.300"
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.700">
                  Notes
                </FormLabel>
                <Textarea
                  value={editingItem?.lab_notes || ''}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, lab_notes: e.target.value }))}
                  placeholder="Any notes about this test for the lab"
                  rows={4}
                  borderColor="gray.300"
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.200" pt={4}>
            <Button mr={3} variant="outline" onClick={onEditClose}>
              Cancel
            </Button>
            <Button 
              colorScheme={editingItem?.type === 'test' ? 'blue' : 'purple'}
              onClick={handleSaveEdit}
              bg={editingItem?.type === 'test' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              }
              color="white"
              _hover={{ opacity: 0.9 }}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LabTestCostsManagement; 